import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Loader2, X, Check, Volume2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', emoji: '🌅' },
  { id: 'lunch', label: 'Lunch', emoji: '☀️' },
  { id: 'dinner', label: 'Dinner', emoji: '🌙' },
  { id: 'snack', label: 'Snack', emoji: '🍎' }
];

export default function VoiceMealLogger({ childId, onMealLogged }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedMeal, setParsedMeal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mealType, setMealType] = useState('snack');
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check for Web Speech API support
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setIsSupported(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-GB';

    recognitionRef.current.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      setTranscript(prev => prev + finalTranscript + interimTranscript);
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        toast.error('No speech detected. Please try again.');
      } else if (event.error === 'audio-capture') {
        toast.error('No microphone found. Please check your device.');
      } else if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      }
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    if (!recognitionRef.current) return;
    
    setTranscript('');
    setParsedMeal(null);
    setShowModal(true);
    
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (err) {
      console.error('Failed to start recognition:', err);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  const parseTranscript = async () => {
    if (!transcript.trim()) {
      toast.error('Please say something first');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${API}/voice-meal/parse`,
        { transcript: transcript.trim(), child_id: childId },
        { headers: getAuthHeaders() }
      );
      
      setParsedMeal(response.data);
    } catch (err) {
      toast.error('Failed to parse meal. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const logMeal = async () => {
    if (!parsedMeal) return;

    setIsProcessing(true);
    try {
      await axios.post(
        `${API}/voice-meal/log`,
        {
          child_id: childId,
          meal_type: mealType,
          foods: parsedMeal.foods,
          totals: parsedMeal.totals
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Meal logged successfully! 🎉');
      setShowModal(false);
      resetState();
      if (onMealLogged) onMealLogged();
    } catch (err) {
      toast.error('Failed to log meal');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetState = () => {
    setTranscript('');
    setParsedMeal(null);
    setMealType('snack');
  };

  if (!isSupported) {
    return (
      <Button
        disabled
        className="bg-zinc-700 text-zinc-400 cursor-not-allowed"
        data-testid="voice-meal-btn-disabled"
      >
        <MicOff className="h-4 w-4 mr-2" />
        Voice (Not Supported)
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={startListening}
        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
        data-testid="voice-meal-btn"
      >
        <Mic className="h-4 w-4 mr-2" />
        Voice Log
      </Button>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full">
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isListening 
                    ? 'bg-red-500/20 animate-pulse' 
                    : 'bg-gradient-to-r from-purple-500/20 to-pink-500/20'
                }`}>
                  {isListening ? (
                    <Volume2 className="h-5 w-5 text-red-500 animate-pulse" />
                  ) : (
                    <Mic className="h-5 w-5 text-purple-500" />
                  )}
                </div>
                <div>
                  <h2 className="font-bold text-white">Voice Meal Logger</h2>
                  <p className="text-xs text-zinc-400">
                    {isListening ? 'Listening... Speak clearly' : 'Click mic to start'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => { setShowModal(false); stopListening(); resetState(); }} 
                className="text-zinc-500 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Recording Controls */}
              <div className="text-center py-4">
                <button
                  onClick={isListening ? stopListening : startListening}
                  className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all ${
                    isListening
                      ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/50'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/30'
                  }`}
                  data-testid="voice-record-btn"
                >
                  {isListening ? (
                    <MicOff className="h-8 w-8 text-white" />
                  ) : (
                    <Mic className="h-8 w-8 text-white" />
                  )}
                </button>
                <p className="text-xs text-zinc-500 mt-3">
                  {isListening ? 'Tap to stop' : 'Tap to start recording'}
                </p>
              </div>

              {/* Transcript */}
              <div>
                <label className="text-xs text-zinc-500 mb-2 block">What you said:</label>
                <div className="bg-zinc-800 rounded-lg p-4 min-h-[80px]">
                  {transcript ? (
                    <p className="text-white">{transcript}</p>
                  ) : (
                    <p className="text-zinc-600 italic">
                      Example: "I had two slices of toast with butter and a glass of orange juice"
                    </p>
                  )}
                </div>
              </div>

              {/* Parse Button */}
              {transcript && !parsedMeal && (
                <Button
                  onClick={parseTranscript}
                  disabled={isProcessing}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                  data-testid="parse-voice-btn"
                >
                  {isProcessing ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing...</>
                  ) : (
                    'Analyze Food'
                  )}
                </Button>
              )}

              {/* Parsed Results */}
              {parsedMeal && (
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      Foods Identified
                    </h4>
                    <div className="space-y-2">
                      {parsedMeal.foods.map((food, i) => (
                        <div key={i} className="flex justify-between items-center bg-zinc-800/50 rounded-lg p-2">
                          <div>
                            <p className="text-white font-medium">{food.name}</p>
                            <p className="text-xs text-zinc-500">{food.quantity}</p>
                          </div>
                          <div className="text-right text-xs">
                            <div className="text-white">{food.calories} cal</div>
                            <div className="text-zinc-500">{food.protein}g protein</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-white mb-3">Total Nutrition</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div>
                        <div className="text-xl font-bold text-white">{parsedMeal.totals.calories}</div>
                        <div className="text-xs text-zinc-400">Calories</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-red-400">{parsedMeal.totals.protein}g</div>
                        <div className="text-xs text-zinc-400">Protein</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-yellow-400">{parsedMeal.totals.carbs}g</div>
                        <div className="text-xs text-zinc-400">Carbs</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-400">{parsedMeal.totals.fat}g</div>
                        <div className="text-xs text-zinc-400">Fat</div>
                      </div>
                    </div>
                  </div>

                  {/* Meal Type */}
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Add to Meal</label>
                    <div className="grid grid-cols-4 gap-2">
                      {MEAL_TYPES.map(type => (
                        <button
                          key={type.id}
                          onClick={() => setMealType(type.id)}
                          className={`p-2 rounded-lg text-center transition-all ${
                            mealType === type.id
                              ? 'bg-purple-500/20 border-2 border-purple-500 text-white'
                              : 'bg-zinc-800 border-2 border-transparent text-zinc-400'
                          }`}
                        >
                          <span className="text-lg">{type.emoji}</span>
                          <p className="text-xs mt-1">{type.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetState}
                      className="flex-1 border-zinc-700"
                    >
                      Try Again
                    </Button>
                    <Button
                      onClick={logMeal}
                      disabled={isProcessing}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                      data-testid="log-voice-meal-btn"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Log Meal
                    </Button>
                  </div>
                </div>
              )}

              {/* Hint */}
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-zinc-500 mt-0.5" />
                  <p className="text-xs text-zinc-500">
                    <strong className="text-zinc-400">Tip:</strong> Be specific about quantities. 
                    Say "two eggs" instead of "eggs", or "a glass of milk" instead of just "milk".
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
